import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import {
  hideCanvasSelectLabel,
  isCanvasSelectLabelTarget,
} from "../../utils/canvasSelectLabel";
import {
  canvasOverlayAncestors,
  measureCanvasOverlays,
  canvasLabelPosition,
} from "../../utils/canvasOverlayGeometry";
import VariantBuilderCanvasOutline from "./VariantBuilderCanvasOutline";

const selectedTarget = (selection) => {
  if (!selection?.object_id) return null;
  const id = CSS.escape(String(selection.object_id));
  const selectors = {
    elements: `.cf-element[data-element-id="${id}"]`,
    sections: selection.column
      ? `.cf-column[data-section-id="${id}"][data-column-position="${CSS.escape(
          selection.column
        )}"]`
      : `.cf-section[data-section-id="${id}"]`,
    section_columns: `.cf-flex-column[data-section-column-id="${id}"]`,
    steps: `.canvas[data-step-id="${id}"]`,
    fields: `.cf-form-field[data-field-id="${id}"], .cf-survey-field[data-field-id="${id}"]`,
  };
  const canvas = document.querySelector("#builder");
  return canvas && selectors[selection.object_type]
    ? canvas.querySelector(selectors[selection.object_type])
    : null;
};

const VariantBuilderCanvasSelect = ({ selection, revision }) => {
  const [labelState, setLabelState] = useState(null);
  const [geometry, setGeometry] = useState(null);
  const labelRef = useRef(null);

  useEffect(() => {
    const handleShowLabel = ({ detail }) => {
      if (!detail?.target) return;
      setLabelState((current) =>
        current?.target === detail.target &&
        current.object_type === detail.object_type &&
        current.object_id === detail.object_id &&
        current.label === detail.label
          ? current
          : detail
      );
    };
    const handleHideLabel = ({ detail }) => {
      setLabelState((current) =>
        !detail?.source || current?.object_type === detail.source
          ? null
          : current
      );
    };
    document.addEventListener("showCanvasSelectLabel", handleShowLabel);
    document.addEventListener("hideCanvasSelectLabel", handleHideLabel);
    return () => {
      document.removeEventListener("showCanvasSelectLabel", handleShowLabel);
      document.removeEventListener("hideCanvasSelectLabel", handleHideLabel);
    };
  }, []);

  const trackedTarget = labelState?.target;
  useLayoutEffect(() => {
    const selected = selectedTarget(selection);
    const targets = [selected, trackedTarget].map((target) => ({
      target,
      ancestors: canvasOverlayAncestors(target),
    }));
    if (!selected && !trackedTarget) {
      setGeometry(null);
      return;
    }
    let frame = null;
    let previousKey;
    const measure = () => {
      const boundaries = [
        ...document.querySelectorAll("[data-canvas-boundary]"),
      ];
      const next = measureCanvasOverlays(targets, boundaries);
      next.sharedTarget = selected === trackedTarget;
      const labelRect = labelRef.current?.getBoundingClientRect();
      next.label = canvasLabelPosition(
        next.outlines[1],
        next.viewport,
        labelRect?.width,
        labelRect?.height
      );
      const key = JSON.stringify(next);
      if (key !== previousKey) {
        previousKey = key;
        setGeometry(next);
      }
      if (trackedTarget && !trackedTarget.isConnected) setLabelState(null);
    };
    const tick = () => {
      frame = null;
      measure();
      if (targets.some(({ target }) => target?.isConnected))
        frame = requestAnimationFrame(tick);
    };
    const zoomable = document.querySelector("#cf-zoomable-canvas");
    const observer = new MutationObserver(measure);
    if (zoomable)
      observer.observe(zoomable, {
        attributes: true,
        attributeFilter: ["style"],
      });
    window.addEventListener("resize", measure);
    tick();
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", measure);
      cancelAnimationFrame(frame);
    };
  }, [
    trackedTarget,
    selection?.object_type,
    selection?.object_id,
    selection?.column,
    revision,
  ]);

  const selectObject = (event) => {
    event.stopPropagation();
    dispatchCustomEvent("selectObject", {
      object_type: labelState.object_type,
      object_id: labelState.object_id,
      tab: labelState.tab,
      collapse: labelState.column,
      editing: false,
      panel_closed: false,
    });
  };

  const handleContextMenu = (e) => {
    e.preventDefault();
    if (labelState.popover === false) return;
    document.dispatchEvent(
      new CustomEvent("showPopover", {
        detail: {
          object_type: labelState.object_type,
          object_id: labelState.object_id,
          column: labelState.column,
          collapse: labelState.column,
          label: labelState.label,
          object: labelState.object,
          tab: labelState.tab,
          element_id: labelState.element_id,
          anchorPosition: { x: e.clientX, y: e.clientY },
        },
      })
    );
  };

  const handleMouseLeave = (e) => {
    const related = e.relatedTarget?.nodeType ? e.relatedTarget : null;
    if (
      !e.currentTarget.contains(related) &&
      !labelState.target?.contains(related) &&
      !isCanvasSelectLabelTarget(related)
    ) {
      hideCanvasSelectLabel();
    }
  };

  const handleCogClick = (e) => {
    e.stopPropagation();
    document.dispatchEvent(
      new CustomEvent("showPopover", {
        detail: {
          object_type: labelState.object_type,
          object_id: labelState.object_id,
          column: labelState.column,
          collapse: labelState.column,
          label: labelState.label,
          object: labelState.object,
          tab: labelState.tab,
          element_id: labelState.element_id,
          anchorPosition: { x: e.clientX, y: e.clientY },
        },
      })
    );
  };

  if (!geometry) return null;
  const { viewport, outlines } = geometry;
  const localRect = (rect) =>
    rect && {
      ...rect,
      top: rect.top - viewport.top,
      left: rect.left - viewport.left,
    };
  const label = capitalizeFirstLetter(labelState?.label || "").replace(
    /_/g,
    " "
  );

  return (
    <div
      id="builder-canvas-overlays"
      style={{
        position: "fixed",
        ...viewport,
        overflow: "hidden",
        pointerEvents: "none",
        zIndex: 8,
      }}
    >
      <VariantBuilderCanvasOutline rect={localRect(outlines[0])} />
      {!geometry.sharedTarget && (
        <VariantBuilderCanvasOutline rect={localRect(outlines[1])} />
      )}
      {labelState && outlines[1] && (
        <div
          ref={labelRef}
          className="select-label left"
          data-object_type={labelState.object_type}
          data-object_id={labelState.object_id}
          data-column={labelState.column}
          onContextMenu={handleContextMenu}
          onMouseLeave={handleMouseLeave}
          style={{
            alignItems: "center",
            background: "#003FFF",
            borderRadius: "5px 0px 5px 0px",
            color: "#fff",
            cursor: "pointer",
            justifyContent: "space-between",
            display: "flex",
            fontSize: "11px",
            lineHeight: "15px",
            minHeight: "18px",
            padding: "2px 2px 2px 6px",
            position: "absolute",
            top: geometry.label ? geometry.label.top - viewport.top : 0,
            left: geometry.label ? geometry.label.left - viewport.left : 0,
            visibility: geometry.label ? "visible" : "hidden",
            pointerEvents: "auto",
            maxWidth: viewport.width,
            whiteSpace: "nowrap",
            overflow: "hidden",
          }}
        >
          <span
            onClick={selectObject}
            style={{
              marginRight: "5px",
            }}
            className="select-label-span"
            data-object_id={labelState.object_id}
          >
            {label}
          </span>
          {labelState.popover !== false && (
            <span
              onClick={handleCogClick}
              style={{
                color: "white",
                cursor: "pointer",
                fontSize: "12px",
                transition: "opacity 0.2s ease",
              }}
              title="Options"
              className="-mt-[.5px] -mb-1 mr-1"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="10"
                height="10"
                viewBox="0 0 20 20"
                fill="none"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M11.8626 1.46252C11.3875 -0.487508 8.61246 -0.487508 8.13744 1.46252C8.06652 1.75555 7.92742 2.02769 7.73146 2.2568C7.53549 2.48591 7.2882 2.66552 7.00971 2.78101C6.73122 2.89649 6.4294 2.9446 6.1288 2.92141C5.82821 2.89822 5.53733 2.80438 5.27985 2.64754C3.5648 1.60253 1.60224 3.56506 2.64727 5.28008C3.32229 6.3876 2.72352 7.83262 1.46348 8.13888C-0.487828 8.61263 -0.487828 11.3889 1.46348 11.8614C1.75659 11.9324 2.02878 12.0717 2.2579 12.2678C2.48701 12.4639 2.66657 12.7113 2.78195 12.99C2.89733 13.2686 2.94527 13.5706 2.92187 13.8712C2.89846 14.1719 2.80438 14.4628 2.64727 14.7202C1.60224 16.4353 3.5648 18.3978 5.27985 17.3528C5.53729 17.1957 5.82818 17.1016 6.12886 17.0782C6.42954 17.0548 6.73149 17.1027 7.01013 17.2181C7.28877 17.3335 7.53623 17.513 7.73234 17.7421C7.92845 17.9712 8.06768 18.2434 8.13869 18.5365C8.61246 20.4878 11.3888 20.4878 11.8613 18.5365C11.9326 18.2436 12.0719 17.9716 12.2681 17.7426C12.4642 17.5137 12.7116 17.3342 12.9902 17.2189C13.2688 17.1036 13.5706 17.0556 13.8712 17.0788C14.1718 17.1021 14.4627 17.1959 14.7201 17.3528C16.4352 18.3978 18.3978 16.4353 17.3527 14.7202C17.1959 14.4627 17.102 14.1719 17.0788 13.8713C17.0555 13.5707 17.1035 13.2689 17.2189 12.9903C17.3342 12.7118 17.5136 12.4644 17.7426 12.2682C17.9716 12.072 18.2436 11.9327 18.5365 11.8614C20.4878 11.3877 20.4878 8.61138 18.5365 8.13888C18.2434 8.06787 17.9712 7.92864 17.7421 7.73253C17.513 7.53642 17.3334 7.28897 17.2181 7.01033C17.1027 6.7317 17.0547 6.42975 17.0781 6.12908C17.1015 5.8284 17.1956 5.53751 17.3527 5.28008C18.3978 3.56506 16.4352 1.60253 14.7201 2.64754C14.4627 2.80465 14.1718 2.89873 13.8711 2.92213C13.5705 2.94554 13.2685 2.8976 12.9899 2.78222C12.7112 2.66684 12.4638 2.48728 12.2677 2.25817C12.0715 2.02906 11.9323 1.75688 11.8613 1.46377L11.8626 1.46252ZM10 13.7502C10.9946 13.7502 11.9485 13.3551 12.6517 12.6518C13.355 11.9486 13.7501 10.9947 13.7501 10.0002C13.7501 9.00558 13.355 8.05174 12.6517 7.34846C11.9485 6.64519 10.9946 6.2501 10 6.2501C9.00541 6.2501 8.05155 6.64519 7.34827 7.34846C6.64498 8.05174 6.24988 9.00558 6.24988 10.0002C6.24988 10.9947 6.64498 11.9486 7.34827 12.6518C8.05155 13.3551 9.00541 13.7502 10 13.7502Z"
                  fill="white"
                />
              </svg>
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default VariantBuilderCanvasSelect;
